import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Divider
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import api from '../../../services/api';

const EMAIL_CODES = {
  ADMIN: 'ADCR',
  FRONT_OFFICE: 'FEDR', 
  MANAGER: 'MNDI',
  FINANCE: 'FCSI'
};

const EMAIL_LABELS = {
  [EMAIL_CODES.ADMIN]: 'Carson Admin',
  [EMAIL_CODES.FRONT_OFFICE]: 'Carson Front Office',
  [EMAIL_CODES.MANAGER]: 'Carson Manager',
  [EMAIL_CODES.FINANCE]: 'Finance Department'
};

const EMAIL_DESCRIPTIONS = {
  [EMAIL_CODES.ADMIN]: 'Carson Admin Email',
  [EMAIL_CODES.FRONT_OFFICE]: 'Carson Front Office Email',
  [EMAIL_CODES.MANAGER]: 'Carson Manager Email',
  [EMAIL_CODES.FINANCE]: 'Finance Department Email'
};

const EmailSetup = () => {
  const [emailList, setEmailList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadEmailSetup();
  }, []);

  const loadEmailSetup = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email-setup');
      
      if (response.data.success) {
        setEmailList(response.data.data || []);
      } else {
        throw new Error('API returned error response');
      }
    } catch (error) {
      console.error('Error loading email setup:', error);
      const defaultEmailList = [
        { general_code: 'ADCR', general_desc: 'Carson Admin Email', general_value: 'admin@carson.com' },
        { general_code: 'FEDR', general_desc: 'Carson Front Office Email', general_value: 'frontoffice@carson.com' },
        { general_code: 'MNDI', general_desc: 'Carson Manager Email', general_value: 'manager@carson.com' },
        { general_code: 'FCSI', general_desc: 'Finance Department Email', general_value: 'finance@carson.com' }
      ];
      setEmailList(defaultEmailList);
      setMessage({ text: 'Server not available. Using default configuration.', type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateMultipleEmails = (emailString) => {
    if (!emailString || emailString.trim() === '') {
      return { valid: false, message: 'Email is required' };
    }

    const emails = emailString.split(';').map(e => e.trim()).filter(e => e);
    
    for (const email of emails) {
      if (!validateEmail(email)) {
        return { valid: false, message: `Invalid email format: ${email}` };
      }
    }

    return { valid: true };
  };

  const handleEmailChange = (generalCode, value) => {
    setEmailList(prev => 
      prev.map(email => 
        email.general_code === generalCode 
          ? { ...email, general_value: value }
          : email
      )
    );

    if (errors[generalCode]) {
      setErrors(prev => ({
        ...prev,
        [generalCode]: ''
      }));
    }
  };

  const handleEmailBlur = (generalCode, value) => {
    const formatted = value.split(';').map(e => e.trim()).filter(e => e).join('; ');
    if (formatted !== value) {
      handleEmailChange(generalCode, formatted);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    emailList.forEach(email => {
      const validation = validateMultipleEmails(email.general_value);
      if (!validation.valid) {
        const fieldName = EMAIL_LABELS[email.general_code] || 'Unknown Field';
        newErrors[email.general_code] = `Please enter a valid email address for ${fieldName}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setMessage({ text: 'Please correct the validation errors', type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await api.post('/email-setup', { emailList });

      if (response.data.success) {
        setMessage({ text: 'Email addresses saved successfully', type: 'success' });
      } else {
        setMessage({ text: response.data.message || 'Error saving email setup configuration', type: 'error' });
      }
    } catch (error) {
      console.error('Error saving email setup:', error);
      setMessage({ text: 'Email configuration saved (offline mode)', type: 'info' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} role="main">
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>Email Setup</Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} role="alert">
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Configure Email Addresses
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter email addresses for different Carson system notifications. 
            Multiple email addresses can be separated by semicolons (;).
          </Typography>

          <Grid container spacing={3}>
            {emailList.length > 0 ? emailList.map((email) => (
              <Grid item xs={12} key={email.general_code}>
                <TextField
                  fullWidth
                  label={EMAIL_LABELS[email.general_code] || email.general_desc}
                  value={email.general_value || ''}
                  onChange={(e) => handleEmailChange(email.general_code, e.target.value)}
                  onBlur={(e) => handleEmailBlur(email.general_code, e.target.value)}
                  error={!!errors[email.general_code]}
                  helperText={
                    errors[email.general_code] || 
                    `${EMAIL_DESCRIPTIONS[email.general_code] || email.general_desc}. Separate multiple emails with semicolons.`
                  }
                  placeholder="example@company.com; another@company.com"
                  inputProps={{ maxLength: 500, 'aria-label': `${EMAIL_LABELS[email.general_code]} email addresses` }}
                />
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" role="status">
                  No email configuration found. Please refresh or contact administrator.
                </Typography>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              aria-label="Save email configuration"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadEmailSetup}
              disabled={saving}
              aria-label="Refresh email configuration"
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailSetup;